/**
 * 飞舟 H5 ↔ App Bridge 统一调用层
 *
 *   import { call, fire, requestAccessToken, requestUserInfo } from '@/utils/feizhou-bridge'
 *   import { initAuth, requireAuth } from '@/utils/urlAuth'  // 页面鉴权入口
 *
 * 降级：window.feizhou → flutter_inappwebview → legacy postMessage
 */

/**
 * 老版本 Bridge 名称 → 全局对象名映射
 * 老客户端通过 window.MeetingBridge.postMessage / window.FileBridge.postMessage / window.Toast.postMessage 通信
 */
const LEGACY_BRIDGE_MAP = {
  MeetingBridge: 'MeetingBridge',
  FileBridge: 'FileBridge',
  Toast: 'Toast',
}

/**
 * 调用原生 Bridge，自动选择可用通道。
 * @param {string} name    Handler 名称 (Toast, AuthBridge, RouteBridge, MeetingBridge, ...)
 * @param {*}      payload 参数 (对象或字符串)
 * @returns {Promise<any>} 原生返回结果；走老桥兜底时返回 null
 */
export async function call(name, payload) {
  const body = typeof payload === 'string' ? payload : JSON.stringify(payload)

  // 1) 新容器统一入口 (Flutter 注入 / PC Electron webview-bridge.ts / vote.ts)
  if (typeof window.feizhou?.callHandler === 'function') {
    const raw = await window.feizhou.callHandler(name, body)
    return typeof raw === 'string' ? tryParse(raw) : raw
  }

  // 2) Flutter 旧容器 (flutter_inappwebview)
  if (typeof window.flutter_inappwebview?.callHandler === 'function') {
    const raw = await window.flutter_inappwebview.callHandler(name, body)
    return typeof raw === 'string' ? tryParse(raw) : raw
  }

  // 3) 老版本客户端兜底：window.xxBridge.postMessage(body)
  //    postMessage 无返回值，call() 只能 resolve null
  if (tryLegacyPostMessage(name, body)) {
    return null
  }

  // 4) PC Web/WASM 父窗口 iframe 宿主
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    return callWebHostBridge(name, payload)
  }

  console.warn(`[feizhou-bridge] 无可用 Bridge 通道: ${name}`)
  return null
}

/** 与 window.feizhou.callHandler 命名保持一致的兼容别名。 */
export const callHandler = call

/** fire-and-forget 调用（不等待返回值） */
export function fire(name, payload) {
  const body = typeof payload === 'string' ? payload : JSON.stringify(payload)

  if (typeof window.feizhou?.callHandler === 'function') {
    window.feizhou.callHandler(name, body)
    return
  }
  if (typeof window.flutter_inappwebview?.callHandler === 'function') {
    window.flutter_inappwebview.callHandler(name, body)
    return
  }

  // 老版本兜底
  if (tryLegacyPostMessage(name, body)) {
    return
  }
  if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
    void callWebHostBridge(name, payload).catch(() => undefined)
    return
  }

  console.warn(`[feizhou-bridge] 无可用 Bridge 通道: ${name}`)
}

/** 订阅 App/PC Native 推送事件；旧容器不支持事件时返回空取消函数。 */
export function on(event, handler) {
  if (typeof window.feizhou?.on === 'function') {
    return window.feizhou.on(event, handler)
  }
  return () => {}
}

/** 取消 App/PC Native 推送事件订阅。 */
export function off(event, handler) {
  if (typeof window.feizhou?.off === 'function') {
    window.feizhou.off(event, handler)
  }
}

function currentUserAgent() {
  if (typeof window !== 'undefined' && window.navigator?.userAgent) {
    return window.navigator.userAgent
  }
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return navigator.userAgent
  }
  return ''
}

/** 是否在飞舟 App WebView 内（UA / 环境标记）。 */
export function isInFeizhouApp() {
  return (
    /FeizhouApp\//i.test(currentUserAgent()) ||
    (typeof window !== 'undefined' && window.__FEIZHOU_APP__?.client === 'feizhou-app')
  )
}

/**
 * PC Electron WebView：通道是 preload 的 window.feizhou，没有 flutter_inappwebview。
 * 主判断 bridgeSdk（契约字段）；UA 含 Electron/ 作兜底（App UA 只有 FeizhouApp/，没有 Electron）。
 */
export function isElectronWebview() {
  if (typeof window !== 'undefined' && window.__FEIZHOU_APP__?.bridgeSdk === 'electron-webview') {
    return true
  }
  return /Electron\//i.test(currentUserAgent())
}

/**
 * 原生通道是否真正可调用。
 * App 内 window.feizhou 会在 DOCUMENT_START 注入，但底层 flutter_inappwebview
 * 往往稍后才就绪；此时直接 callHandler 会抛错，导致丢 token。
 *
 * PC 同样把 client 设为 feizhou-app，但不能按 App 去等 flutter_inappwebview。
 */
export function isBridgeReady() {
  if (typeof window.flutter_inappwebview?.callHandler === 'function') {
    return true
  }
  if (typeof window.feizhou?.callHandler !== 'function') {
    return false
  }
  // PC：preload 注入的 feizhou 即可调用
  if (isElectronWebview()) {
    return true
  }
  // 纯浏览器 / 非 App 容器：有 feizhou 即可
  if (!isInFeizhouApp()) {
    return true
  }
  // 真 App：还要等 flutter_inappwebview
  return false
}

/** 检查是否有可用 Bridge（含老版本兜底；不保证已 ready） */
export function isAvailable() {
  return !!(
    window.feizhou?.callHandler ||
    window.flutter_inappwebview?.callHandler ||
    hasLegacyBridge()
  )
}

/**
 * 等待原生 Bridge 真正可用（解决 App 首屏竞态）。
 * 这是上限不是必等时长：通道已就绪会立刻返回。
 * App 上 flutter_inappwebview 通常几十到几百毫秒就绪，2s 足够；不是 SLA。
 * @param {number} [timeoutMs=2000]
 * @returns {Promise<boolean>}
 */
export function waitForBridge(timeoutMs = 2000) {
  if (isBridgeReady()) return Promise.resolve(true)
  // 非 App/无 Bridge 线索：不必空等
  if (!isAvailable() && !isInFeizhouApp()) {
    return Promise.resolve(false)
  }

  return new Promise((resolve) => {
    const start = Date.now()
    const tick = () => {
      if (isBridgeReady()) {
        resolve(true)
        return
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(isBridgeReady() || isAvailable())
        return
      }
      setTimeout(tick, 50)
    }
    tick()
  })
}

/**
 * 获取当前可用的 Bridge 通道类型
 * @returns {'feizhou'|'flutter_inappwebview'|'legacy'|null}
 */
export function getChannelType() {
  if (typeof window.feizhou?.callHandler === 'function') return 'feizhou'
  if (typeof window.flutter_inappwebview?.callHandler === 'function') return 'flutter_inappwebview'
  if (hasLegacyBridge()) return 'legacy'
  return null
}

/**
 * 等通道就绪后再 call（业务优选入口；底层变更只改本文件）。
 * @param {string} name
 * @param {*} payload
 * @param {{ timeoutMs?: number }} [opts]
 */
export async function callWhenReady(name, payload, opts = {}) {
  await waitForBridge(opts.timeoutMs ?? 2000)
  return call(name, payload)
}

/**
 * AuthBridge.requestAccessToken
 * @returns {Promise<{ feizhouToken: string, imToken: string, chatToken: string, imUserId: string, spaceId: string, raw: any }>}
 */
export async function requestAccessToken(opts = {}) {
  const res = await callWhenReady('AuthBridge', { action: 'requestAccessToken' }, opts)
  const data = res?.data || {}
  return {
    feizhouToken: data.feizhouToken || '',
    imToken: data.imToken || '',
    chatToken: data.chatToken || '',
    imUserId: data.imUserId || '',
    spaceId: data.spaceId || '',
    raw: res,
  }
}

/**
 * AuthBridge.requestUserInfo
 * @returns {Promise<{ imUserId: string, spaceId: string, userName: string, avatar: string, data: Record<string, any>, raw: any }>}
 */
export async function requestUserInfo(opts = {}) {
  const res = await callWhenReady('AuthBridge', { action: 'requestUserInfo' }, opts)
  const data = res?.data || {}
  return {
    imUserId: data.imUserId || '',
    spaceId: data.spaceId || '',
    userName: data.userName || '',
    avatar: data.avatar || '',
    data,
    raw: res,
  }
}

/**
 * AppBridge.getEnvironment
 * @returns {Promise<{ theme: string, locale: string, platform: string, version: string, data: Record<string, any>, raw: any }>}
 */
export async function getEnvironment(opts = {}) {
  const res = await callWhenReady('AppBridge', { action: 'getEnvironment' }, opts)
  const data = res?.data || {}
  return {
    theme: data.theme === 'dark' || data.theme === 'light' ? data.theme : '',
    locale: data.locale || '',
    platform: data.platform || data.platformRaw || '',
    version: data.version || '',
    data,
    raw: res,
  }
}

/**
 * 通用通讯录选人。返回 null 表示取消，[] 表示用户明确清空。
 * 宿主负责可信 origin、当前身份、人数上限和群/系统号限制。
 */
export async function pickContacts({
  purpose = 'report.receiver',
  selectedImUserIds = [],
  excludeImUserIds = [],
  maxCount = 100,
  title = '选择汇报对象',
  requestId = newRequestId()
} = {}) {
  if (!['report.receiver', 'report.cc'].includes(purpose)) {
    throw Object.assign(new Error('不支持的选人用途'), { code: 'PURPOSE_DENIED' })
  }
  const requestPayload = {
    action: 'pick',
    data: {
      requestId,
      purpose,
      selectedImUserIds: selectedImUserIds.map(String),
      excludeImUserIds: excludeImUserIds.map(String),
      maxCount: Math.max(1, Math.min(Number(maxCount) || 100, 100)),
      title
    }
  }
  let response = null
  if (isAvailable()) {
    response = await callWhenReady('ContactBridge', requestPayload)
  } else if (window.parent && window.parent !== window) {
    response = await requestContactFromWebHost(requestPayload.data)
  }
  if (!response || typeof response !== 'object') {
    throw Object.assign(new Error('当前客户端不支持通讯录选人，请在飞舟客户端填写'), {
      code: 'CONTACT_PICKER_UNAVAILABLE'
    })
  }
  if (response.data?.requestId && response.data.requestId !== requestId) {
    throw Object.assign(new Error('选人结果与请求不匹配'), { code: 'REQUEST_MISMATCH' })
  }
  if (response.action === 'cancel') return null
  if (response.action === 'fail') {
    throw Object.assign(new Error(response.data?.message || '选人失败'), {
      code: response.data?.code || 'CONTACT_PICK_FAILED'
    })
  }
  if (response.action !== 'success' || response.data?.status !== 'confirmed') {
    throw Object.assign(new Error('选人返回格式无效'), { code: 'INVALID_RESPONSE' })
  }
  return (response.data.targets || []).map((target) => ({
    imUserId: String(target.imUserId),
    displayName: target.displayName || target.name || '',
    avatarUrl: target.avatarUrl || target.avatar || '',
    source: target.source || ''
  }))
}

/**
 * App 原生选择图片/文件，但不在 Bridge 内上传。
 * H5 拿到 Base64 后写入业务后端签发的受控上传地址，适用于私有附件。
 */
export async function pickFilesForPrivateUpload({
  kind = 'file',
  maxCount = 1
} = {}) {
  if (!['image', 'file'].includes(kind)) {
    throw Object.assign(new Error('不支持的附件类型'), { code: 'UNSUPPORTED_KIND' })
  }
  const action = kind === 'image' ? 'pickImageForUpload' : 'pickFileForUpload'
  const response = await callWhenReady(
    'MediaBridge',
    { action, maxCount: Math.max(1, Math.min(Number(maxCount) || 1, 9)) },
    { timeoutMs: 120_000 }
  )
  if (!response || typeof response !== 'object') {
    throw Object.assign(new Error('当前客户端不支持原生附件选择'), {
      code: 'MEDIA_PICKER_UNAVAILABLE'
    })
  }
  if (response.action === 'cancel') return null
  if (response.action === 'fail') {
    throw Object.assign(
      new Error(response.data?.message || '附件选择失败'),
      { code: response.data?.code || 'MEDIA_PICK_FAILED' }
    )
  }
  if (response.action !== 'success' || !Array.isArray(response.data?.files)) {
    throw Object.assign(new Error('附件选择返回格式无效'), {
      code: 'INVALID_RESPONSE'
    })
  }
  return response.data.files.map((file) => ({
    kind: String(file.kind || kind),
    fileName: String(file.fileName || ''),
    mimeType: String(file.mimeType || 'application/octet-stream'),
    size: Number(file.size || 0),
    data: String(file.data || '')
  }))
}

function requestContactFromWebHost(data) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(Object.assign(new Error('选人超时，请重试'), { code: 'TIMEOUT' }))
    }, 5 * 60 * 1000)
    function onMessage(event) {
      if (
        event.source !== window.parent ||
        event.data?.type !== 'feizhou:contact-pick-result' ||
        event.data?.data?.requestId !== data.requestId
      ) {
        return
      }
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      const result = event.data.data
      resolve({
        action: result.status === 'cancel' ? 'cancel' : 'success',
        data: result
      })
    }
    window.addEventListener('message', onMessage)
    window.parent.postMessage(
      { type: 'feizhou:contact-pick-request', data },
      '*'
    )
  })
}

let webHostRequestSequence = 0

function callWebHostBridge(name, payload) {
  const requestId = `web-${Date.now()}-${++webHostRequestSequence}`
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(Object.assign(new Error('客户端通信超时'), { code: 'WEB_HOST_TIMEOUT' }))
    }, 20_000)
    function onMessage(event) {
      if (
        event.source !== window.parent ||
        event.data?.type !== 'feizhou:bridge-response' ||
        event.data?.data?.requestId !== requestId
      ) {
        return
      }
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      const response = event.data.data
      if (response?.error) {
        reject(
          Object.assign(new Error(response.error.message || '客户端调用失败'), {
            code: response.error.code || 'WEB_HOST_FAILED'
          })
        )
        return
      }
      resolve(tryParse(response?.response))
    }
    window.addEventListener('message', onMessage)
    window.parent.postMessage(
      {
        type: 'feizhou:bridge-request',
        data: { requestId, name, payload }
      },
      '*'
    )
  })
}

function newRequestId() {
  if (globalThis.crypto?.randomUUID) return crypto.randomUUID()
  return `req-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

// ──────────────── 内部工具 ────────────────

/**
 * 尝试通过老版本 window.xxBridge.postMessage() 发送消息
 * @param {string} name Bridge 名称 (MeetingBridge, FileBridge, Toast, ...)
 * @param {string} body JSON 字符串
 * @returns {boolean} 是否成功发送
 */
function tryLegacyPostMessage(name, body) {
  // 已知的老桥名称直接查找
  const globalName = LEGACY_BRIDGE_MAP[name]
  if (globalName) {
    const bridge = window[globalName]
    if (bridge && typeof bridge.postMessage === 'function') {
      try {
        bridge.postMessage(body)
        console.log(`[feizhou-bridge] 兜底: window.${globalName}.postMessage()`)
        return true
      } catch (e) {
        console.warn(`[feizhou-bridge] window.${globalName}.postMessage() 失败:`, e)
        return false
      }
    }
  }

  // 未知 Bridge 名：尝试 window[name + 'Bridge'] 或 window[name] 约定
  // 例如 name='VoteBridge' → window.VoteBridge?.postMessage
  const fallbackName = name.endsWith('Bridge') ? name : name
  const fallbackBridge = window[fallbackName]
  if (fallbackBridge && typeof fallbackBridge.postMessage === 'function') {
    try {
      fallbackBridge.postMessage(body)
      console.log(`[feizhou-bridge] 兜底: window.${fallbackName}.postMessage()`)
      return true
    } catch (e) {
      console.warn(`[feizhou-bridge] window.${fallbackName}.postMessage() 失败:`, e)
    }
  }

  return false
}

/** 检查是否存在任何老版本 Bridge */
function hasLegacyBridge() {
  return Object.values(LEGACY_BRIDGE_MAP).some(
    (name) => window[name] && typeof window[name].postMessage === 'function'
  )
}

function tryParse(v) {
  try { return JSON.parse(v) } catch (_) { return v }
}
